/**
 * packageWizard.js - Unified Pinia store for package creation/editing wizard
 *
 * Handles all three flows:
 *   - 'scratch': create a new package from scratch
 *   - 'template': create a package from a template
 *   - 'edit': edit an existing draft package
 *
 * Steps: 1. Package Details, 2. Documents, 3. Review Detected Tags, 4. Signers & Review
 */
import { defineStore } from 'pinia';
import signApi from '../services/signApi';
import { uploadFileChunked } from '../services/chunkedUploadService';
import { validatePackage } from '../services/packageValidation';
import { getTenantTimeZone } from '../services/dateFormat';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_CYCLES = 400; // ~20 minutes

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Extract the date portion ("YYYY-MM-DD") from an ISO datetime string
 * returned by the server. Works regardless of 'Z' suffix or timezone offset.
 * Used when loading an existing package into the date picker.
 */
function toDateStr(isoStr) {
    if (!isoStr) return null;
    return isoStr.substring(0, 10); // "2026-03-16T00:00:00Z" → "2026-03-16"
}

/**
 * Today's date as "YYYY-MM-DD" in the tenant's timezone (UTC fallback).
 * Used to validate user-supplied date-only fields against the same wall
 * clock the server applies. Re-evaluated on every call so a long-running
 * tab still picks up the new "today" after midnight.
 */
function todayInTenantTz() {
    const tz = getTenantTimeZone() || 'UTC';
    // 'en-CA' formats date parts as YYYY-MM-DD with hyphens.
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(new Date());
}

/**
 * Returns an error message when the wizard's basicInfo.expirationDate is
 * set to a past date (in tenant TZ), otherwise null. Empty/null values
 * are treated as "no expiration" (allowed). Malformed strings are passed
 * through (server-side validation owns format enforcement); we only own
 * the past-date check here.
 */
function computeExpirationDateError(state) {
    const exp = state.basicInfo?.expirationDate;
    if (!exp) return null;
    if (!ISO_DATE_RE.test(exp)) return null;
    if (exp < todayInTenantTz()) {
        return 'Expiration date cannot be in the past.';
    }
    return null;
}

export const usePackageWizardStore = defineStore('packageWizard', {
    state: () => ({
        // Mode: 'scratch' | 'template' | 'edit'
        mode: null,

        // Template source (template mode only)
        templateId: null,
        templateRoles: [],

        // Package ID (set after draft is created or loaded)
        packageId: null,

        // Original package status (for edit mode context)
        packageStatus: null,

        // Current wizard step (1-4)
        step: 1,

        // Step 1: Package Details
        basicInfo: {
            name: '',
            emailSubject: '',
            emailMessage: '',
            expirationDate: null,
            enforceSigningOrder: true,
            requiresPayment: false,
            paymentAmount: null,
            paymentCurrency: 'usd',
            paymentDescription: '',
            reminderEnabled: false,
            reminderIntervalDays: 3,
            // Instant Bank Payments toggle. When true:
            //   1. Exactly one signer can be the payer (IsPayer + PaymentAmount > 0)
            //   2. enforceSigningOrder is locked on (sequential signing required)
            //   3. The payer must be the last signer in routingOrder
            // The toggle gating + auto-adjustments live in PackagesBuilder.vue's
            // ENABLE-IBP modal flow. Server-side validation rejects mismatched
            // configs at send-time as defense-in-depth.
            enableInstantBankTransfer: false
        },

        // Step 2: Documents
        documents: [],

        // Step 3: Detected tags per document
        detectedTagsByDoc: {},  // { docGuid: { detectedFormat, tags, unrecognizedTagCount } }

        // Step 4: Signers & Annotations
        signers: [],
        annotations: [],

        // Document pages for FieldEditor
        documentsWithPages: [],

        // Loading states
        loading: false,
        uploadingDocument: false,
        uploadProgress: 0,
        sending: false,
        saving: false,
        loadingPages: false,

        // Polling state
        _pollTimer: null,
        _pollCount: 0,
        _isPolling: false,
        _tagRetries: {},  // { docId: retryCount }

        // Error state
        error: null,
        serverValidationErrors: [],

        // Free-tier saved-PM overage consent payload from a 402 response on
        // /send. When non-null, the consuming view should open the
        // PerPackagePrepayDialog in saved-pm mode with this payload as the
        // `consent-preview` prop. Cleared on next sendPackage() invocation
        // and after the dialog completes.
        overageConsentPreview: null
    }),

    getters: {
        /**
         * Whether we are editing an existing package (vs creating)
         */
        isEditing: (state) => state.mode === 'edit',

        /**
         * Whether we are editing a package that is currently Scheduled
         */
        isScheduledEdit: (state) => state.mode === 'edit' && state.packageStatus === 'Scheduled',

        /**
         * Step 1 → 2 gate: package name required, expiration date (if any) not in the past.
         */
        canProceedToStep2: (state) => {
            if (!state.basicInfo.name || state.basicInfo.name.trim().length === 0) return false;
            if (computeExpirationDateError(state)) return false;
            return true;
        },

        /**
         * Error message for the expiration-date field. Surfaced inline on the
         * q-input in Step 1 and also feeds the Continue-button tooltip.
         * Null when the value is empty or a valid future / today date.
         */
        expirationDateError: (state) => computeExpirationDateError(state),

        /**
         * Step 2 → 3 gate: at least one document, all in final state
         */
        canProceedToStep3: (state) => {
            return state.documents.length > 0 &&
                state.documents.every(doc =>
                    doc.processingStatus === 'Ready' || doc.processingStatus === 'Failed');
        },

        /**
         * Step 3 → 4 gate: all docs scanned and none still processing
         */
        canProceedToStep4: (state) => {
            if (state.documents.length === 0) return false;
            // All docs must have been scanned for tags
            const allScanned = state.documents.every(doc => doc.id in state.detectedTagsByDoc);
            const noneProcessing = state.documents.every(doc =>
                doc.processingStatus === 'Ready' || doc.processingStatus === 'Failed');
            return allScanned && noneProcessing;
        },

        /**
         * Whether all documents have been scanned for tags
         */
        allDocsScanned: (state) => {
            return state.documents.length > 0 &&
                state.documents.every(doc => doc.id in state.detectedTagsByDoc);
        },

        /**
         * Whether all documents are in a final state (Ready or Failed)
         */
        allDocsReady: (state) => {
            return state.documents.length > 0 &&
                state.documents.every(doc =>
                    doc.processingStatus === 'Ready' || doc.processingStatus === 'Failed');
        },

        /**
         * Whether any documents are still processing
         */
        hasProcessingDocs: (state) => {
            return state.documents.some(doc =>
                doc.processingStatus !== 'Ready' && doc.processingStatus !== 'Failed');
        },

        /**
         * Documents the backend successfully scanned and found zero embedded text tags.
         *
         * Drives the "No Embedded Tags Found" reassurance dialog on Step 3 → Step 4.
         * MUST exclude `detectionFailed: true` rows, because for those documents we
         * don't actually know whether tags were present - the scan threw. Lumping them
         * into this list shows the user the reassurance copy ("This is normal if you
         * didn't embed tags...") on a doc whose tags MAY have been silently dropped,
         * which is the exact failure mode this whole fix is meant to surface.
         */
        docsWithNoTags: (state) => {
            return state.documents.filter(doc =>
                doc.id in state.detectedTagsByDoc &&
                !state.detectedTagsByDoc[doc.id].detectionFailed &&
                (state.detectedTagsByDoc[doc.id].tags?.length || 0) === 0
            );
        },

        /**
         * Sorted unique signer indices found across all detected tags
         */
        detectedSignerIndices: (state) => {
            const indices = new Set();
            Object.values(state.detectedTagsByDoc).forEach(result => {
                (result.tags || []).forEach(tag => {
                    if (tag.signerIndex != null) indices.add(tag.signerIndex);
                });
            });
            return Array.from(indices).sort((a, b) => a - b);
        },

        /**
         * Total number of detected tags across all documents
         */
        totalDetectedTags: (state) => {
            return Object.values(state.detectedTagsByDoc)
                .reduce((sum, result) => sum + (result.tags?.length || 0), 0);
        },

        /**
         * Whether any document has unrecognized/unmatched tags
         */
        hasUnmatchedTags: (state) => {
            return Object.values(state.detectedTagsByDoc)
                .some(result => (result.unrecognizedTagCount || 0) > 0);
        },

        /**
         * Whether any non-group signer has a placeholder email
         */
        hasPlaceholderSigners: (state) => {
            return state.signers.some(s => !s.signingGroupId && /@pending\.local$/i.test(s.email));
        },

        /**
         * Tooltip text explaining why the Continue button is disabled for a given step.
         * Returns null if the button should be enabled.
         */
        stepBlockReason: (state) => {
            return (step) => {
                switch (step) {
                    case 1: {
                        if (!state.basicInfo.name?.trim()) return 'Package name is required';
                        const expErr = computeExpirationDateError(state);
                        if (expErr) return expErr;
                        return null;
                    }
                    case 2: {
                        if (state.documents.length === 0) return 'Upload at least one document';
                        const processing = state.documents.some(d =>
                            d.processingStatus !== 'Ready' && d.processingStatus !== 'Failed');
                        if (processing) return 'Documents are still processing';
                        return null;
                    }
                    case 3: {
                        if (state.documents.some(d =>
                            d.processingStatus !== 'Ready' && d.processingStatus !== 'Failed'))
                            return 'Some documents are still processing';
                        if (!state.documents.every(doc => doc.id in state.detectedTagsByDoc))
                            return 'Waiting for tag detection results';
                        return null;
                    }
                    default:
                        return null;
                }
            };
        },

        /**
         * Reason the Send button is disabled in Step 4.
         * Returns null if sending is allowed.
         */
        sendDisabledReason: (state) => {
            if (!state.basicInfo.name?.trim()) return 'Package name is required';
            if (state.documents.length === 0) return 'At least one document is required';
            if (state.documents.some(d =>
                d.processingStatus !== 'Ready' && d.processingStatus !== 'Failed'))
                return 'Some documents are still being processed';
            if (state.signers.length === 0) return 'At least one signer is required';
            // Skip placeholder/missing checks for group-based signers (they have auto-generated emails)
            const nonGroupSigners = state.signers.filter(s => !s.signingGroupId);
            const placeholder = nonGroupSigners.find(s => /@pending\.local$/i.test(s.email));
            if (placeholder)
                return `Signer "${placeholder.name || placeholder.roleName || '(unnamed)'}" has a placeholder email that must be updated`;
            const missing = nonGroupSigners.find(s => !s.name?.trim() || !s.email?.trim());
            if (missing)
                return `Signer "${missing.name || '(unnamed)'}" is missing required information`;
            // Signers/InPersonSigners must have at least one annotation
            const annotations = state.annotations || [];
            const noFields = state.signers.find(s =>
                (s.type === 'Signer' || s.type === 'InPersonSigner') &&
                !annotations.some(a => a.signerIndex === s.routingOrder));
            if (noFields)
                return `Signer "${noFields.name || noFields.roleName || '(unnamed)'}" has no fields assigned`;
            return null;
        },

        /**
         * Whether the package can be sent
         */
        canSend() {
            return !!this.packageId && !this.sendDisabledReason;
        },

        /**
         * Client-side validation result for the current package state
         */
        validationResult: (state) => {
            return validatePackage(state);
        },

        /**
         * Total required fields count
         */
        requiredFieldsCount: (state) => {
            return state.annotations.filter(a => a.required).length;
        },

        /**
         * Available annotation types for dropdowns
         */
        annotationTypes: () => [
            { label: 'Signature', value: 'Signature', icon: 'draw' },
            { label: 'Initials', value: 'Initials', icon: 'short_text' },
            { label: 'Date Signed', value: 'DateSigned', icon: 'calendar_today' },
            { label: 'Text', value: 'Text', icon: 'text_fields' },
            { label: 'Checkbox', value: 'Checkbox', icon: 'check_box' },
            { label: 'Dropdown', value: 'Dropdown', icon: 'arrow_drop_down' }
        ],

        /**
         * Signer options for dropdowns (index-based)
         */
        signerOptions: (state) => {
            return state.signers.map((s, index) => ({
                label: s.name ? `${s.name} (${s.email || 'no email'})` : `Signer ${index + 1}`,
                value: index
            }));
        },

        /**
         * Annotations grouped by document
         */
        annotationsByDocument: (state) => {
            const grouped = {};
            for (const doc of state.documents) {
                grouped[doc.id] = state.annotations.filter(a => a.documentId === doc.id);
            }
            return grouped;
        }
    },

    actions: {
        // ───── Initialization ─────

        /**
         * Initialize for creating a package from scratch.
         */
        initFromScratch() {
            this._reset();
            this.mode = 'scratch';
        },

        /**
         * Initialize for creating a package from a template.
         * Fetches template info and roles, pre-fills basicInfo.
         * @param {string} templateId - Template GUID
         */
        async initFromTemplate(templateId) {
            this._reset();
            this.mode = 'template';
            this.templateId = templateId;
            this.loading = true;
            this.error = null;

            try {
                const [templateResponse, rolesResponse] = await Promise.all([
                    signApi.templates.get(templateId),
                    signApi.templates.listRoles(templateId)
                ]);

                const template = templateResponse.data;

                if (template.isActive === false) {
                    this.error = 'This template is inactive and cannot be used to create packages. Edit the template to reactivate it.';
                    return;
                }

                this.templateRoles = rolesResponse.data || [];

                // Pre-fill basic info from template
                this.basicInfo.name = template.name ? `${template.name} - Copy` : '';
                this.basicInfo.emailSubject = template.emailSubject || '';
                this.basicInfo.emailMessage = template.emailMessage || '';
                this.basicInfo.enforceSigningOrder = template.enforceSigningOrder ?? true;
                if (template.defaultPaymentAmount) {
                    this.basicInfo.requiresPayment = true;
                    this.basicInfo.paymentAmount = template.defaultPaymentAmount;
                    this.basicInfo.paymentDescription = template.defaultPaymentDescription || '';
                }
            } catch (err) {
                console.error('[PackageWizard] Failed to load template:', err);
                this.error = err.response?.data?.message || err.displayMessage || 'Failed to load template';
            } finally {
                this.loading = false;
            }
        },

        /**
         * Load an existing package for editing.
         * @param {string} packageId - Package GUID
         */
        async loadPackage(packageId) {
            this._reset();
            this.mode = 'edit';
            this.loading = true;
            this.error = null;

            try {
                const [pkgResponse, docsResponse, signersResponse, annotationsResponse] = await Promise.all([
                    signApi.packages.get(packageId),
                    signApi.documents.list(packageId),
                    signApi.signers.list(packageId),
                    signApi.annotations.list(packageId)
                ]);

                const pkg = pkgResponse.data;
                this.packageId = pkg.id;
                this.packageStatus = pkg.status;
                this.basicInfo = {
                    name: pkg.name,
                    emailSubject: pkg.emailSubject || '',
                    emailMessage: pkg.emailMessage || '',
                    expirationDate: pkg.expirationDateDisplay || toDateStr(pkg.expirationDate),
                    enforceSigningOrder: pkg.enforceSigningOrder,
                    requiresPayment: pkg.requiresPayment,
                    paymentAmount: pkg.paymentAmount,
                    paymentCurrency: pkg.paymentCurrency || 'usd',
                    paymentDescription: pkg.paymentDescription || '',
                    reminderEnabled: pkg.reminderEnabled || false,
                    reminderIntervalDays: pkg.reminderIntervalDays || 3,
                    enableInstantBankTransfer: pkg.enableInstantBankTransfer || false
                };
                this.documents = docsResponse.data || [];
                this.signers = signersResponse.data || [];
                this.annotations = annotationsResponse.data || [];

                // Fetch detected tags for docs that are already processed
                await this._fetchDetectedTagsForReadyDocs();

                // Mark docs that have annotations as already scanned
                // so Step 3 shows them as complete
                if (this.annotations.length > 0) {
                    for (const doc of this.documents) {
                        if (!(doc.id in this.detectedTagsByDoc)) {
                            const docAnnotations = this.annotations.filter(a => a.documentId === doc.id);
                            if (docAnnotations.length > 0) {
                                this.detectedTagsByDoc[doc.id] = { tags: docAnnotations, detectedFormat: 'saved' };
                            }
                        }
                    }
                }

                // Start polling if any docs are still processing
                if (this.hasProcessingDocs) {
                    this._startPolling();
                }

                return pkg;
            } catch (err) {
                console.error('[PackageWizard] Failed to load package:', err);
                this.error = err.response?.data?.message || err.displayMessage || 'Failed to load package';
                return null;
            } finally {
                this.loading = false;
            }
        },

        // ───── Step 1: Save / Create Package ─────

        /**
         * Save package basic info (create or update depending on mode).
         */
        async saveBasicInfo() {
            if (this.loading) return false; // prevent duplicate submissions
            this.loading = true;
            this.error = null;

            try {
                const data = {
                    name: this.basicInfo.name,
                    emailSubject: this.basicInfo.emailSubject || null,
                    emailMessage: this.basicInfo.emailMessage || null,
                    enforceSigningOrder: this.basicInfo.enforceSigningOrder,
                    expirationDate: this.basicInfo.expirationDate,
                    // Payment is now per-signer - don't send package-level payment fields
                    // (kept for backward compat: existing packages retain their values)
                    paymentCurrency: this.basicInfo.paymentCurrency,
                    paymentDescription: this.basicInfo.paymentDescription || null,
                    reminderEnabled: this.basicInfo.reminderEnabled,
                    reminderIntervalDays: this.basicInfo.reminderEnabled ? this.basicInfo.reminderIntervalDays : null,
                    enableInstantBankTransfer: this.basicInfo.enableInstantBankTransfer
                };

                if (this.packageId) {
                    // Update existing package
                    await signApi.packages.update(this.packageId, data);
                } else if (this.mode === 'template' && this.templateId) {
                    // Reject a roleless template here with a specific message, rather
                    // than letting the generic "at least one role mapping is required"
                    // server error surface. A template with zero signers can never
                    // produce a valid package - the user needs to edit the template.
                    if (!this.templateRoles || this.templateRoles.length === 0) {
                        this.error = 'This template has no signer roles defined. Open the template in the editor and add at least one role before creating a package from it.';
                        return false;
                    }
                    const roleMappings = this.templateRoles.map(r => ({
                        roleName: r.name,
                        signerName: '',
                        signerEmail: ''
                    }));

                    const response = await signApi.templates.createPackage(this.templateId, {
                        packageName: data.name,
                        emailSubject: data.emailSubject,
                        emailMessage: data.emailMessage,
                        enforceSigningOrder: data.enforceSigningOrder,
                        expirationDate: data.expirationDate,
                        reminderEnabled: data.reminderEnabled,
                        reminderIntervalDays: data.reminderIntervalDays,
                        roleMappings
                    });
                    this.packageId = response.data.packageId;

                    // Update URL so page refresh loads the created package instead of re-running template init
                    this._replaceUrlToEdit(this.packageId);

                    // Load full package data (docs, signers, annotations created from template)
                    await this._loadPackageData();
                } else {
                    // Create new empty draft
                    const response = await signApi.packages.create(data);
                    this.packageId = response.data.id;

                    // Update URL so page refresh loads the created package instead of re-running scratch init
                    this._replaceUrlToEdit(this.packageId);
                }

                return true;
            } catch (err) {
                console.error('[PackageWizard] Failed to save basic info:', err);
                this.error = err.response?.data?.message || err.displayMessage || 'Failed to save package';
                return false;
            } finally {
                this.loading = false;
            }
        },

        // ───── Step 2: Documents ─────

        /**
         * Upload a document to the package.
         * @param {File} file - File to upload
         * @param {Object} [options] - Upload options
         */
        async uploadDocument(file, options = {}) {
            if (!this.packageId) {
                this.error = 'Package must be created first';
                return null;
            }

            this.uploadingDocument = true;
            this.uploadProgress = 0;
            this.error = null;

            try {
                const commitResult = await uploadFileChunked(this.packageId, file, {
                    displayName: options.displayName,
                    displayOrder: options.displayOrder,
                    onProgress: ({ percent }) => { this.uploadProgress = percent; }
                });

                const doc = {
                    id: commitResult.documentGuid,
                    name: options.displayName || file.name.replace(/\.[^/.]+$/, ''),
                    originalFileName: file.name,
                    processingStatus: commitResult.processingStatus,
                    fileSizeBytes: commitResult.fileSizeBytes
                };
                this.documents.push(doc);

                // Start polling if document needs background processing
                if (doc.processingStatus !== 'Ready' && doc.processingStatus !== 'Failed') {
                    this._startPolling();
                } else if (doc.processingStatus === 'Ready') {
                    // Doc processed immediately - fetch tags now
                    await this.fetchDetectedTags(doc.id);
                }

                return doc;
            } catch (err) {
                console.error('[PackageWizard] Failed to upload document:', err);
                this.error = err.response?.data?.message || err.displayMessage || 'Failed to upload document';
                return null;
            } finally {
                this.uploadingDocument = false;
                this.uploadProgress = 0;
            }
        },

        /**
         * Remove a document from the package.
         * @param {string} documentId - Document GUID
         */
        async removeDocument(documentId) {
            if (!this.packageId) return false;

            this.loading = true;
            this.error = null;

            try {
                await signApi.documents.delete(this.packageId, documentId);
                this.documents = this.documents.filter(d => d.id !== documentId);
                // Clean up related state
                delete this.detectedTagsByDoc[documentId];
                this.annotations = this.annotations.filter(a => a.documentId !== documentId);
                this.documentsWithPages = this.documentsWithPages.filter(d => d.documentId !== documentId);

                if (!this.hasProcessingDocs) {
                    this._stopPolling();
                }

                return true;
            } catch (err) {
                console.error('[PackageWizard] Failed to remove document:', err);
                this.error = err.response?.data?.message || err.displayMessage || 'Failed to remove document';
                return false;
            } finally {
                this.loading = false;
            }
        },

        /**
         * Check the impact of removing a document (orphaned signers).
         * @param {string} documentId - Document GUID
         * @returns {Promise<{orphanedSigners: Array}|null>}
         */
        async getDocumentRemovalImpact(documentId) {
            if (!this.packageId) return null;
            try {
                const response = await signApi.documents.getRemovalImpact(this.packageId, documentId);
                return response.data;
            } catch (err) {
                console.warn('[PackageWizard] Failed to get removal impact:', err);
                return null;
            }
        },

        /**
         * Remove orphaned signers after document deletion (best-effort).
         * @param {Array<{id: string}>} orphanedSigners
         * @returns {number} Count of signers that failed to remove (0 = all succeeded)
         */
        async _removeOrphanedSigners(orphanedSigners) {
            let failCount = 0;
            for (const signer of orphanedSigners) {
                try {
                    await signApi.signers.remove(this.packageId, signer.id);
                    const removed = this.signers.find(s => s.id === signer.id);
                    this.signers = this.signers.filter(s => s.id !== signer.id);
                    if (removed) {
                        this.annotations = this.annotations.filter(a => a.signerIndex !== removed.routingOrder);
                    }
                } catch (err) {
                    failCount++;
                    console.warn('[PackageWizard] Failed to remove orphaned signer:', signer.id, err);
                }
            }
            return failCount;
        },

        // ───── Step 3: Tag Detection ─────

        /**
         * Fetch detected tags for a single document.
         * @param {string} documentId - Document GUID
         */
        async fetchDetectedTags(documentId) {
            if (!this.packageId) return null;

            try {
                const response = await signApi.documents.getDetectedTags(this.packageId, documentId);
                const result = response.data;
                this.detectedTagsByDoc[documentId] = result;
                return result;
            } catch (err) {
                // 404 means tags haven't been detected yet - not an error
                if (err.response?.status === 404) {
                    return null;
                }
                console.error('[PackageWizard] Failed to fetch detected tags for doc', documentId, ':', err);
                return null;
            }
        },

        /**
         * Safety net: ensures tag fetching is active for the current document set.
         * Called when entering Step 3 to handle cases where polling missed or was never started.
         */
        async ensureTagsLoaded() {
            // Fetch tags for any Ready docs that haven't been scanned yet
            await this._fetchDetectedTagsForReadyDocs();

            // Start polling if there are still processing docs or unscanned docs
            if (this.hasProcessingDocs || !this.allDocsScanned) {
                this._startPolling();
            }
        },

        /**
         * Fetch detected tags for all Ready documents that don't have tag data yet.
         *
         * Backend contract: once the Functions orchestrator commits the document to
         * Ready it ALSO writes a DetectedTagsJson row - either a successful scan or
         * a `detectionFailed:true` sentinel from MarkTagDetectionFailed. So a 404 on
         * a Ready doc should be rare and short-lived (the commit-race window between
         * `ProcessingStatus = Ready` and the JSON-column save inside the same DbContext).
         *
         * After MAX_TAG_RETRIES consecutive 404s we no longer pretend "0 tags found."
         * Doing so was the original silent-tag-loss bug: a real backend failure or a
         * legacy NULL row (pre-fix data) became indistinguishable from a clean 0-tag
         * scan. Instead we surface a `detectionFailed:true` sentinel locally so the
         * existing per-doc UI branch renders "Could not scan for tags" - same visible
         * outcome the customer would have gotten if the backend sentinel had landed.
         */
        async _fetchDetectedTagsForReadyDocs() {
            const MAX_TAG_RETRIES = 5;
            const readyDocs = this.documents.filter(
                doc => doc.processingStatus === 'Ready' && !(doc.id in this.detectedTagsByDoc));

            for (const doc of readyDocs) {
                const result = await this.fetchDetectedTags(doc.id);
                if (!result) {
                    this._tagRetries[doc.id] = (this._tagRetries[doc.id] || 0) + 1;
                    if (this._tagRetries[doc.id] >= MAX_TAG_RETRIES) {
                        console.error(`[PackageWizard] Ready document ${doc.id} returned 404 after ${MAX_TAG_RETRIES} retries. Backend sentinel write may have failed, or this is a legacy doc from before the fix shipped. Surfacing as detectionFailed so the user sees a real failure state instead of a silent "0 tags" result.`);
                        this.detectedTagsByDoc[doc.id] = {
                            detectedFormat: 'None',
                            tags: [],
                            unrecognizedTagCount: 0,
                            detectionFailed: true,
                            failureReason: 'Tag detection result is unavailable for this document.'
                        };
                    }
                }
            }
        },

        // ───── Step 4: Signers ─────

        /**
         * Add a signer.
         * @param {Object} signerData - Signer data
         */
        async addSigner(signerData) {
            if (!this.packageId) {
                this.error = 'Package must be created first';
                return null;
            }

            this.loading = true;
            this.error = null;

            try {
                const response = await signApi.signers.add(this.packageId, signerData);
                const signer = response.data;
                this.signers.push(signer);
                return signer;
            } catch (err) {
                console.error('[PackageWizard] Failed to add signer:', err);
                this.error = err.response?.data?.message || err.displayMessage || 'Failed to add signer';
                return null;
            } finally {
                this.loading = false;
            }
        },

        /**
         * Add multiple signers at once (atomic, all-or-nothing).
         * @param {Array<Object>} signersData - Array of signer data objects
         * @returns {Array|null} Created signers or null on error
         */
        async addSignersBulk(signersData) {
            if (!this.packageId) {
                this.error = 'Package must be created first';
                return null;
            }

            this.loading = true;
            this.error = null;

            try {
                const response = await signApi.signers.addBulk(this.packageId, signersData);
                const created = response.data.signers;
                this.signers.push(...created);
                return created;
            } catch (err) {
                console.error('[PackageWizard] Failed to bulk add signers:', err);
                this.error = err.response?.data?.message || err.displayMessage || 'Failed to add signers';
                return null;
            } finally {
                this.loading = false;
            }
        },

        /**
         * Update a signer.
         * @param {string} signerId - Signer GUID
         * @param {Object} signerData - Updated data
         */
        async updateSigner(signerId, signerData) {
            if (!this.packageId) return null;

            this.loading = true;
            this.error = null;

            try {
                const response = await signApi.signers.update(this.packageId, signerId, signerData);
                const updated = response.data;
                const index = this.signers.findIndex(s => s.id === signerId);
                if (index >= 0) {
                    this.signers[index] = updated;
                }
                return updated;
            } catch (err) {
                console.error('[PackageWizard] Failed to update signer:', err);
                this.error = err.response?.data?.message || err.displayMessage || 'Failed to update signer';
                return null;
            } finally {
                this.loading = false;
            }
        },

        /**
         * Remove a signer.
         * @param {string} signerId - Signer GUID
         */
        async removeSigner(signerId) {
            if (!this.packageId) return false;

            this.loading = true;
            this.error = null;

            try {
                await signApi.signers.remove(this.packageId, signerId);
                const removedSigner = this.signers.find(s => s.id === signerId);
                this.signers = this.signers.filter(s => s.id !== signerId);
                // Remove annotations assigned to this signer by routingOrder (not array index)
                if (removedSigner) {
                    this.annotations = this.annotations.filter(a => a.signerIndex !== removedSigner.routingOrder);
                }
                return true;
            } catch (err) {
                console.error('[PackageWizard] Failed to remove signer:', err);
                this.error = err.response?.data?.message || err.displayMessage || 'Failed to remove signer';
                return false;
            } finally {
                this.loading = false;
            }
        },

        /**
         * Reorder signers (update routing order).
         * @param {Array} newOrder - Array of signer IDs in new order
         */
        async reorderSigners(newOrder) {
            if (!this.packageId) return false;

            // Optimistically update local state so the UI reorders immediately
            const signerMap = Object.fromEntries(this.signers.map(s => [s.id, s]));
            for (let i = 0; i < newOrder.length; i++) {
                if (signerMap[newOrder[i]]) {
                    signerMap[newOrder[i]].routingOrder = i + 1;
                }
            }
            this.signers = [...this.signers].sort((a, b) => a.routingOrder - b.routingOrder);

            this.loading = true;
            this.error = null;

            try {
                // Single batch call to reorder all signers at once
                const reorderPayload = newOrder.map((signerId, i) => ({ signerId, routingOrder: i + 1 }));
                await signApi.signers.reorder(this.packageId, reorderPayload);

                // Refresh signers and annotations in parallel
                const [signersResponse] = await Promise.all([
                    signApi.signers.list(this.packageId),
                    this._loadAnnotations()
                ]);
                this.signers = signersResponse.data;
                return true;
            } catch (err) {
                console.error('[PackageWizard] Failed to reorder signers:', err);
                this.error = err.response?.data?.message || err.displayMessage || 'Failed to reorder signers';
                // Refresh to revert optimistic update on failure
                try {
                    const response = await signApi.signers.list(this.packageId);
                    this.signers = response.data;
                } catch { /* ignore refresh error */ }
                return false;
            } finally {
                this.loading = false;
            }
        },

        // ───── Step 4: Annotations ─────

        /**
         * Apply detected text tags as annotations for all documents.
         * Idempotent - skips documents that already have annotations.
         */
        async applyDetectedTags() {
            if (!this.packageId) return [];

            this.loading = true;
            this.error = null;

            try {
                const response = await signApi.annotations.applyDetectedTags(this.packageId);
                const result = response.data;
                const created = result?.annotations || [];
                if (created.length > 0) {
                    this.annotations.push(...created);
                }

                // Refresh signers - backend may have created placeholder signers from detected tags
                try {
                    const signersResponse = await signApi.signers.list(this.packageId);
                    this.signers = signersResponse.data || [];
                } catch (signerErr) {
                    console.warn('[PackageWizard] Failed to refresh signers after tag apply:', signerErr);
                }

                return created;
            } catch (err) {
                console.error('[PackageWizard] Failed to apply detected tags:', err);
                // Non-fatal - user can still manually add annotations
                return [];
            } finally {
                this.loading = false;
            }
        },

        /**
         * Add an annotation.
         * @param {Object} annotationData - Annotation data
         */
        async addAnnotation(annotationData) {
            if (!this.packageId) {
                this.error = 'Package must be created first';
                return null;
            }

            this.loading = true;
            this.error = null;

            try {
                const response = await signApi.annotations.create(this.packageId, annotationData);
                const annotation = response.data;
                this.annotations.push(annotation);
                return annotation;
            } catch (err) {
                console.error('[PackageWizard] Failed to add annotation:', err);
                this.error = err.response?.data?.message || err.displayMessage || 'Failed to add field';
                return null;
            } finally {
                this.loading = false;
            }
        },

        /**
         * Add multiple annotations in bulk.
         * @param {Array} annotationsData - Array of annotation data
         */
        async addAnnotationsBulk(annotationsData) {
            if (!this.packageId || !annotationsData.length) return [];

            this.loading = true;
            this.error = null;

            try {
                const response = await signApi.annotations.createBulk(this.packageId, annotationsData);
                const created = response.data;
                this.annotations.push(...created);
                return created;
            } catch (err) {
                console.error('[PackageWizard] Failed to add annotations:', err);
                this.error = err.response?.data?.message || err.displayMessage || 'Failed to add fields';
                return [];
            } finally {
                this.loading = false;
            }
        },

        /**
         * Update an annotation.
         * @param {string} annotationId - Annotation GUID
         * @param {Object} data - Updated data
         */
        async updateAnnotation(annotationId, data) {
            if (!this.packageId) return null;

            this.loading = true;
            this.error = null;

            try {
                const response = await signApi.annotations.update(this.packageId, annotationId, data);
                const updated = response.data;
                const index = this.annotations.findIndex(a => a.id === annotationId);
                if (index >= 0) {
                    this.annotations[index] = updated;
                }
                return updated;
            } catch (err) {
                console.error('[PackageWizard] Failed to update annotation:', err);
                this.error = err.response?.data?.message || err.displayMessage || 'Failed to update field';
                return null;
            } finally {
                this.loading = false;
            }
        },

        /**
         * Remove an annotation.
         * @param {string} annotationId - Annotation GUID
         */
        async removeAnnotation(annotationId) {
            if (!this.packageId) return false;

            this.loading = true;
            this.error = null;

            try {
                await signApi.annotations.delete(this.packageId, annotationId);
                this.annotations = this.annotations.filter(a => a.id !== annotationId);
                return true;
            } catch (err) {
                console.error('[PackageWizard] Failed to remove annotation:', err);
                this.error = err.response?.data?.message || err.displayMessage || 'Failed to remove field';
                return false;
            } finally {
                this.loading = false;
            }
        },

        // ───── Step 4: Document Pages (for FieldEditor) ─────

        /**
         * Load page images for all ready documents.
         */
        async loadDocumentPages() {
            if (!this.packageId || this.documents.length === 0) return;

            this.loadingPages = true;
            this.error = null;

            try {
                const results = await Promise.allSettled(
                    this.documents
                        .filter(d => d.processingStatus === 'Ready')
                        .map(async (doc) => {
                            const response = await signApi.documents.getPages(this.packageId, doc.id);
                            return {
                                documentId: doc.id,
                                name: doc.name || doc.originalFileName,
                                pages: response.data?.pages || []
                            };
                        })
                );
                this.documentsWithPages = results
                    .filter(r => r.status === 'fulfilled' && r.value.pages.length > 0)
                    .map(r => r.value);
            } catch (err) {
                console.error('[PackageWizard] Failed to load document pages:', err);
                this.error = err.response?.data?.message || err.displayMessage || 'Failed to load document pages';
            } finally {
                this.loadingPages = false;
            }
        },

        // ───── Step 4: Send / Schedule ─────

        /**
         * Send the package for signing.
         */
        async sendPackage() {
            if (!this.packageId) {
                this.error = 'Package must be created first';
                return false;
            }

            this.sending = true;
            this.error = null;
            this.serverValidationErrors = [];
            this.overageConsentPreview = null;

            try {
                // Server-side validation (authoritative)
                const pkgValidation = await signApi.packages.validate(this.packageId);
                if (!pkgValidation.data.isValid) {
                    this.serverValidationErrors = pkgValidation.data.errors;
                    return false;
                }

                await signApi.packages.send(this.packageId);
                return true;
            } catch (err) {
                console.error('[PackageWizard] Failed to send package:', err);
                const data = err.response?.data;

                // 402 OverageConsentRequired - Free-tier saved-PM path. The server
                // refuses to silently off-session charge; surface the preview to
                // the view so it can open the consent dialog. We don't treat this
                // as a generic error (no validation message, no error toast).
                if (err.response?.status === 402 && data?.errorCode === 'OverageConsentRequired') {
                    this.overageConsentPreview = {
                        amount: data.amount,
                        taxAmount: data.taxAmount,
                        totalAmount: data.totalAmount,
                        taxJurisdiction: data.taxJurisdiction,
                        currency: data.currency,
                        cardBrand: data.cardBrand,
                        cardLast4: data.cardLast4,
                        description: data.description
                    };
                    return false;
                }

                if (data?.validationErrors?.length) {
                    this.serverValidationErrors = data.validationErrors.map(msg => {
                        const isBilling = /payment/i.test(msg);
                        return {
                            message: isBilling
                                ? `${msg} - Visit Settings > Billing to update your payment method.`
                                : msg
                        };
                    });
                } else {
                    this.error = data?.message || err.displayMessage || 'Failed to send package';
                }
                return false;
            } finally {
                this.sending = false;
            }
        },

        /**
         * Schedule the package for future send.
         * @param {string} sendTime - ISO datetime string for when to send
         */
        async schedulePackage(sendTime) {
            if (!this.packageId) {
                this.error = 'Package must be created first';
                return false;
            }

            this.sending = true;
            this.error = null;
            this.serverValidationErrors = [];

            try {
                const validation = await signApi.packages.validate(this.packageId);
                if (!validation.data.isValid) {
                    this.serverValidationErrors = validation.data.errors;
                    return false;
                }

                await signApi.packages.send(this.packageId, { scheduledSendTime: sendTime });
                return true;
            } catch (err) {
                console.error('[PackageWizard] Failed to schedule package:', err);
                const data = err.response?.data;
                if (data?.validationErrors?.length) {
                    this.serverValidationErrors = data.validationErrors.map(msg => {
                        const isBilling = /payment/i.test(msg);
                        return {
                            message: isBilling
                                ? `${msg} - Visit Settings > Billing to update your payment method.`
                                : msg
                        };
                    });
                } else {
                    this.error = data?.message || err.displayMessage || 'Failed to schedule package';
                }
                return false;
            } finally {
                this.sending = false;
            }
        },

        // ───── Navigation ─────

        /**
         * Move to the next step. Handles auto-save and auto-apply logic at step boundaries.
         */
        async nextStep() {
            if (this.step === 1) {
                const saved = await this.saveBasicInfo();
                if (!saved) return false;
            }
            if (this.step < 4) {
                this.step++;
            }
            return true;
        },

        /**
         * Move to the previous step.
         */
        prevStep() {
            if (this.step > 1) {
                this.step--;
            }
        },

        /**
         * Go to a specific step.
         * @param {number} targetStep - Target step number (1-4)
         */
        goToStep(targetStep) {
            if (targetStep >= 1 && targetStep <= 4) {
                this.step = targetStep;
            }
        },

        /**
         * Clear error state.
         */
        clearError() {
            this.error = null;
            this.serverValidationErrors = [];
        },

        // ───── Polling ─────

        /**
         * Start polling for document processing status updates.
         */
        _startPolling() {
            if (this._pollTimer) return;
            this._pollCount = 0;
            this._pollTimer = setInterval(() => this._pollDocumentStatuses(), POLL_INTERVAL_MS);
        },

        /**
         * Stop polling.
         */
        _stopPolling() {
            if (this._pollTimer) {
                clearInterval(this._pollTimer);
                this._pollTimer = null;
            }
            this._isPolling = false;
        },

        /**
         * Poll document list and update statuses. Auto-fetch tags for newly ready docs.
         */
        async _pollDocumentStatuses() {
            if (this._isPolling) return;
            if (!this.packageId) {
                this._stopPolling();
                return;
            }

            this._pollCount++;
            if (this._pollCount > MAX_POLL_CYCLES) {
                // Don't silently stop - if documents are still processing,
                // mark them as failed so the user doesn't stare at a frozen spinner.
                if (this.hasProcessingDocs) {
                    console.error('[PackageWizard] Processing timed out after', MAX_POLL_CYCLES, 'cycles');
                    for (const doc of this.documents) {
                        if (doc.processingStatus !== 'Ready' && doc.processingStatus !== 'Failed') {
                            doc.processingStatus = 'Failed';
                            doc.processingError = 'Document processing timed out. Please delete and re-upload.';
                        }
                    }
                }
                this._stopPolling();
                return;
            }

            this._isPolling = true;
            try {
                const response = await signApi.documents.list(this.packageId);
                const freshDocs = response.data || [];

                const newlyReady = [];

                for (const freshDoc of freshDocs) {
                    const existing = this.documents.find(d => d.id === freshDoc.id);
                    if (existing) {
                        const wasProcessing = existing.processingStatus !== 'Ready'
                            && existing.processingStatus !== 'Failed';
                        const isNowReady = freshDoc.processingStatus === 'Ready';

                        Object.assign(existing, freshDoc);

                        if (wasProcessing && isNowReady) {
                            newlyReady.push(freshDoc.id);
                        }
                    }
                }

                // Auto-fetch detected tags for newly ready documents
                for (const docId of newlyReady) {
                    await this.fetchDetectedTags(docId);
                }

                // Retry fetching tags for Ready docs that still don't have tags
                if (newlyReady.length === 0) {
                    await this._fetchDetectedTagsForReadyDocs();
                }

                // Stop polling when all docs are in a final state AND all have been scanned
                if (!this.hasProcessingDocs && this.allDocsScanned) {
                    this._stopPolling();
                }
            } catch (err) {
                console.error('[PackageWizard] Polling failed:', err);
            } finally {
                this._isPolling = false;
            }
        },

        // ───── Private Helpers ─────

        /**
         * Load full package data (documents, signers, annotations) after template-based creation.
         */
        async _loadPackageData() {
            if (!this.packageId) return;

            try {
                const [docsResponse, signersResponse, annotationsResponse] = await Promise.all([
                    signApi.documents.list(this.packageId),
                    signApi.signers.list(this.packageId),
                    signApi.annotations.list(this.packageId)
                ]);

                this.documents = docsResponse.data || [];
                this.signers = signersResponse.data || [];
                this.annotations = annotationsResponse.data || [];

                // Fetch detected tags for ready docs
                await this._fetchDetectedTagsForReadyDocs();

                if (this.hasProcessingDocs) {
                    this._startPolling();
                }
            } catch (err) {
                console.error('[PackageWizard] Failed to load package data:', err);
                this.error = err.response?.data?.message || err.displayMessage || 'Failed to load package data';
            }
        },

        /**
         * Refresh annotations from server.
         */
        async _loadAnnotations() {
            if (!this.packageId) return;

            try {
                const response = await signApi.annotations.list(this.packageId);
                this.annotations = response.data || [];
            } catch (err) {
                console.error('[PackageWizard] Failed to load annotations:', err);
            }
        },

        /**
         * After a new package is created, replace the browser URL so that
         * a page refresh loads the existing package instead of re-initializing.
         */
        _replaceUrlToEdit(packageGuid) {
            if (packageGuid && window.history && window.history.replaceState) {
                window.history.replaceState(null, '', `/Packages/${packageGuid}/Edit`);
                window._pageConfig = Object.assign(window._pageConfig || {}, {
                    packageGuid: packageGuid,
                    templateId: undefined
                });
            }
        },

        /**
         * Reset all state to initial values.
         */
        _reset() {
            this._stopPolling();
            this.mode = null;
            this.templateId = null;
            this.templateRoles = [];
            this.packageId = null;
            this.step = 1;
            this.basicInfo = {
                name: '',
                emailSubject: '',
                emailMessage: '',
                expirationDate: null,
                enforceSigningOrder: true,
                requiresPayment: false,
                paymentAmount: null,
                paymentCurrency: 'usd',
                paymentDescription: '',
                reminderEnabled: false,
                reminderIntervalDays: 3,
                enableInstantBankTransfer: false
            };
            this.documents = [];
            this.detectedTagsByDoc = {};
            this._tagRetries = {};
            this.signers = [];
            this.annotations = [];
            this.documentsWithPages = [];
            this.loading = false;
            this.uploadingDocument = false;
            this.uploadProgress = 0;
            this.sending = false;
            this.saving = false;
            this.loadingPages = false;
            this.error = null;
            this.serverValidationErrors = [];
        },

        // Keep legacy alias for backward compatibility during transition
        reset() {
            this._reset();
        }
    }
});
